import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Box, Button, Chip, Divider, Stack, Tabs, Tab, Typography, useMediaQuery, useTheme, alpha } from '@mui/material';
import { PageLayout } from '@ui/components/PageLayout';
import { useHelpTopic } from '@ui/contexts';
import { centeredRowStack } from '@ui/style/style';
import { Edit } from 'iconsax-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { hasPermission } from '@application/helpers/permission-helper';
import { useSelector } from 'react-redux';
import { useSponsorshipProfileStyles } from '../../DetailsTab/components/useSponsorshipProfileStyles';
import Tickets from './Tickets';
import { useAppDispatch } from '@application/store/useStore';
import { getPartnerEventDetailsAsync } from '@application/features/partnerEvent/partnerEventThunk';
import NewEventDialogDrawer from '../NewEvent';
import { useRedirectOnCompanyChange } from '@ui/hooks/useRedirectOnCompanyChange';
const TicketDetail = () => {
    useRedirectOnCompanyChange('/sponsorships');
    useParams();
    const { t } = useTranslation();
    const theme = useTheme();
    const navigate = useNavigate();
    const isSmallScreen = useMediaQuery(theme.breakpoints.down('lg'));
    const styles = useSponsorshipProfileStyles();
    // states here
    const [activeTab, setActiveTab] = useState(0);
    useHelpTopic(activeTab === 0 ? 'ticketDetails' : 'ticketTickets');
    const [newEventOpen, setNewEventOpen] = useState(false);
    const tabLabels = [
        { label: t('sponsorships.details'), value: 0 },
        { label: t('sponsorships.tickets.title'), value: 1 }
    ];
    const { id, eventId } = useParams();
    const { selectedCompanyId } = useSelector((state) => state.company);
    const { PartnerEventDetails } = useSelector((state) => state.partnerEvent);
    const loggedInUserPermissions = useSelector((state) => state.auth.currentUser?.permissions || []);
    const canUpdateEvents = hasPermission(loggedInUserPermissions, 'Permissions.VdkEvents.Update');
    const dispatch = useAppDispatch();
    useEffect(() => {
        if (selectedCompanyId && selectedCompanyId.length > 0 && eventId) {
            dispatch(getPartnerEventDetailsAsync(eventId)).unwrap();
        }
    }, [dispatch, selectedCompanyId, eventId]);
    // status color
    const getStatusColor = (status) => {
        switch (status) {
            case 'Active':
                return 'info';
            case 'Pending':
                return 'warning';
            case 'Expired':
                return 'error';
            case 'Cancelled':
                return 'default';
            default:
                return 'default';
        }
    };
    const calculateEventDuration = (startDate, endDate) => {
        if (!startDate || !endDate) {
            return null;
        }
        const start = new Date(startDate);
        const end = new Date(endDate);
        const timeDifferenceInMs = end.getTime() - start.getTime();
        return Math.ceil(timeDifferenceInMs / (1000 * 3600 * 24)) + 1; // +1 to include the start date
    };
    const formatDate = (date) => {
        if (!date)
            return '';
        const d = new Date(date);
        return d.toLocaleDateString();
    };
    const eventDurationInDays = calculateEventDuration(PartnerEventDetails?.startDate, PartnerEventDetails?.endDate);
    const startDate = formatDate(PartnerEventDetails?.startDate);
    const endDate = formatDate(PartnerEventDetails?.endDate);
    const handleEditEvent = () => {
        if (!eventId)
            return;
        // Check if event is linked (active status) and navigate to create-event page
        const statusStr = PartnerEventDetails?.status?.toString()?.toLowerCase() || '';
        if (statusStr === 'active') {
            navigate(`/create-event?eventId=${eventId}`);
        }
        else {
            // Open the edit dialog for non-linked events
            setNewEventOpen(true);
        }
    };
    const handleNewEventClose = () => setNewEventOpen(false);
    return (_jsxs(PageLayout, { showBackButton: true, onBackClick: () => navigate(-1), title: PartnerEventDetails?.title || t('sponsorships.sponsorshipEventDetails'), secondTitle: t('sponsorships.accessSponsorshipEventDetailsHere'), showTabs: true, tabs: _jsx(Tabs, { value: activeTab, onChange: (_, newValue) => setActiveTab(newValue), children: tabLabels.map((tab) => (_jsx(Tab, { label: tab.label, value: tab.value }, tab.value))) }), rightContent: _jsx(Box, { sx: { ...centeredRowStack }, gap: 1, children: _jsx(Button, { startIcon: _jsx(Edit, {}), variant: 'contained', size: isSmallScreen ? 'medium' : 'large', color: 'primary', disabled: !canUpdateEvents, onClick: handleEditEvent, children: t('events.editEvent') }) }), children: [activeTab === 0 && (_jsx(Box, { children: _jsx(Box, { pt: theme.spacing(1.5), children: _jsxs(Stack, { direction: 'column', sx: {
                            backgroundColor: theme.palette.background.default,
                            borderRadius: theme.spacing(3),
                            border: `1px solid ${alpha(theme.palette.grey[500], 0.24)}`,
                            backdropFilter: 'blur(20px)'
                        }, children: [_jsx(Box, { sx: {
                                    position: 'relative',
                                    width: '100%',
                                    height: 200,
                                    borderRadius: '12px 12px 0 0',
                                    ...styles.profileBackground
                                }, children: _jsx(Box, { component: 'img', src: '/assets/images/ticket-detail-cover-bg.png', alt: 'Background', sx: {
                                        width: '100%',
                                        height: '100%',
                                        objectFit: 'cover',
                                        borderTopLeftRadius: '12px',
                                        borderTopRightRadius: '12px'
                                    } }) }), _jsxs(Stack, { direction: { xs: 'column', md: 'row' }, alignItems: { xs: 'flex-start', md: 'center' }, sx: {
                                    py: theme.spacing(3),
                                    px: theme.spacing(2),
                                    gap: { xs: 2, md: 0 },
                                    width: '100%'
                                }, children: [_jsxs(Stack, { direction: 'row', justifyContent: 'space-between', flex: 1, children: [_jsx(Typography, { variant: 'h5', children: PartnerEventDetails?.title }), _jsx(Box, { sx: { flexShrink: 0 }, children: _jsx(Chip, { variant: 'soft', label: PartnerEventDetails?.status?.toString() === 'Active'
                                                        ? t('common.active')
                                                        : PartnerEventDetails?.status?.toString() === 'Inactive'
                                                            ? t('common.inactive')
                                                            : PartnerEventDetails?.status?.toLocaleString() ?? '', color: PartnerEventDetails?.status?.toLocaleString() === 'Active'
                                                        ? getStatusColor('Active')
                                                        : getStatusColor('Pending'), size: 'small', sx: { height: 24, ml: 2, mt: 0.5 } }) })] }), _jsx(Divider, { orientation: 'vertical', sx: {
                                            mx: 2,
                                            color: theme.palette.divider,
                                            height: 72,
                                            display: { xs: 'none', md: 'block' }
                                        } }), _jsxs(Stack, { direction: 'column', flex: 1, children: [_jsxs(Typography, { variant: 'h6', children: [eventDurationInDays, " days event"] }), _jsxs(Typography, { variant: 'body2', color: 'text.secondary', children: [startDate, " \u2192 ", endDate] })] })] })] }) }) })), activeTab === 1 && _jsx(Tickets, {}), newEventOpen && (_jsx(NewEventDialogDrawer, { open: newEventOpen, onClose: handleNewEventClose, isEdit: true, eventId: eventId }))] }));
};
export default TicketDetail;
